/* Brave Bottoms: Package Protection ($4.49 product "package-protection").
   - Added to the cart automatically when a shopper adds pairs (pre-checked), unless they switched it off for this cart.
   - The switch in the cart drawer (snippets/bb-protection.liquid) removes or adds it again.
     Switching it off saves the cart attribute "Package protection" = "Declined" so it is not added back.
   - Removed automatically when no other items are left in the cart.
   - Its own cart line is hidden in the drawer (bb-head.liquid); the switch row shows it instead. */
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const HANDLE = 'package-protection';
const ATTR = 'Package protection';
const SOURCE = 'bb-protection';
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const json = { 'Content-Type': 'application/json', Accept: 'application/json' };

let busy = false;
let failures = 0;

const getCart = () => fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
const sectionIds = () =>
  Array.from(new Set(Array.from(document.querySelectorAll('cart-items-component'), (el) => el.dataset.sectionId).filter(Boolean)));
const protectionLine = (cart) => cart.items.find((item) => item.handle === HANDLE);
const otherQuantity = (cart) => cart.items.filter((item) => item.handle !== HANDLE).reduce((sum, item) => sum + item.quantity, 0);
const variantId = () => {
  const el = document.querySelector('[data-bb-pp][data-variant]') || document.querySelector('[data-bb-pp-variant]');
  return el ? Number(el.dataset.variant || el.dataset.bbPpVariant) : 0;
};

/* Send a cart change, then let the cart drawer and cart count redraw from the returned sections.
   'update' (not 'add') so the drawer is never opened by this script. */
async function send(url, body) {
  const sections = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  /* Dispatched from body: cart-items-component ignores events dispatched on itself, so it would not redraw. */
  const target = document.body;
  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(`${root()}${url}`, {
      method: 'POST',
      headers: json,
      body: JSON.stringify({ ...body, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Cart update failed');
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: data.sections, items: cart.items, itemCount: cart.item_count, source: SOURCE, sourceId: SOURCE, didError: false },
    });
    return cart;
  } catch (error) {
    try { deferred.reject(error); } catch (e) { /* already settled */ }
    throw error;
  }
}

/* The header cart bubble counts pairs only (theme counts every line, including protection). */
const fixBubble = (cart) => {
  if (!cart || !Array.isArray(cart.items)) return;
  const line = protectionLine(cart);
  const count = cart.item_count - (line ? line.quantity : 0);
  /* After the theme's own bubble update for the same change has run */
  setTimeout(() => {
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble === 'function' && icon.currentCartCount !== count) icon.renderCartBubble(count, false);
    });
  }, 0);
};

const setDeclined = (on) =>
  fetch(`${root()}cart/update.js`, { method: 'POST', headers: json, body: JSON.stringify({ attributes: { [ATTR]: on ? 'Declined' : '' } }) });

/* Keep the cart in the right state: protection on while pairs are in the cart, unless declined. */
async function ensure() {
  if (busy) return;
  busy = true;
  try {
    const cart = await getCart();
    const line = protectionLine(cart);
    const others = otherQuantity(cart);
    const declined = (cart.attributes || {})[ATTR] === 'Declined';
    let after = cart;
    if (others === 0 && line) {
      after = await send('cart/change.js', { id: line.key, quantity: 0 });
    } else if (others > 0 && !line && !declined) {
      const id = variantId();
      if (id) after = await send('cart/add.js', { items: [{ id, quantity: 1 }] });
    } else if (line && line.quantity > 1) {
      after = await send('cart/change.js', { id: line.key, quantity: 1 });
    }
    fixBubble(after);
  } catch (error) {
    failures += 1;
    console.warn('[bb-protection]', error);
  } finally {
    busy = false;
  }
}

/* After any cart change made by the shopper or the theme, re-check (never react to our own changes). */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!event.promise) return;
  event.promise
    .then((result) => {
      const detail = result && result.detail;
      if (!detail || detail.source === SOURCE || detail.didError) return;
      ensure();
    })
    .catch(() => {});
});

/* The drawer snippet marks when the cart needs fixing (data-need), which also covers cart changes made by apps
   that redraw the drawer without the theme event. Checked on load and whenever the drawer is redrawn.
   Stops after 3 failed tries so a failing add can never loop. */
const checkNeed = () => {
  if (busy || failures >= 3) return;
  if (document.querySelector('[data-bb-pp][data-need="add"], [data-bb-pp][data-need="remove"]')) ensure();
};
checkNeed();
new MutationObserver(() => checkNeed()).observe(document.body, { childList: true, subtree: true });

/* The switch */
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-bb-pp-toggle]') : null;
  if (!button || busy) return;
  event.preventDefault();
  const turningOn = button.getAttribute('aria-checked') !== 'true';
  button.setAttribute('aria-checked', turningOn ? 'true' : 'false');
  button.setAttribute('aria-busy', 'true');
  busy = true;
  try {
    await setDeclined(!turningOn);
    const cart = await getCart();
    const line = protectionLine(cart);
    let after = cart;
    if (turningOn && !line) {
      const id = variantId();
      if (id) after = await send('cart/add.js', { items: [{ id, quantity: 1 }] });
    } else if (!turningOn && line) {
      after = await send('cart/change.js', { id: line.key, quantity: 0 });
    }
    fixBubble(after);
  } catch (error) {
    console.warn('[bb-protection]', error);
    button.setAttribute('aria-checked', turningOn ? 'false' : 'true');
  } finally {
    button.removeAttribute('aria-busy');
    busy = false;
  }
});
